import { Question } from './questions';
import { COUNTRIES } from './questions';

// Per-demande-type question definitions
// Keys are the application ID from the `applications` table in Supabase

interface DemandeQuestionSet {
  questions: Record<string, Question>;
  order: string[];
}

export const DEMANDE_QUESTIONS: Record<number, DemandeQuestionSet> = {
  // Application ID 1
  1: {
    questions: {
      // ── Section: Informations Personnelles et Familiales ──

      hasChildren: {
        id: 'hasChildren',
        type: 'yes-no',
        label: 'Avez-vous des enfants ?',
        indication: 'Indiquez si vous avez des enfants, même s\'ils ne vous accompagnent pas au Canada.',
        help: 'Incluez tous vos enfants biologiques ou adoptés, quel que soit leur âge.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Informations Personnelles et Familiales',
      },

      childrenInfo: {
        id: 'childrenInfo',
        type: 'multi-entry',
        label: 'Veuillez remplir les informations relatives à vos enfants',
        indication: 'Ajoutez chaque enfant avec son prénom, nom, date de naissance et pays de naissance.',
        help: 'Incluez tous vos enfants, y compris ceux qui ne vous accompagnent pas.',
        isRequired: true,
        successMessage: 'Informations des enfants enregistrées',
        section: 'Informations Personnelles et Familiales',
        dependsOn: { questionId: 'hasChildren', value: 'oui' },
        multiEntryFields: [
          { key: 'firstName', label: 'Prénom', placeholder: 'Jean', type: 'text' },
          { key: 'lastName', label: 'Nom', placeholder: 'Dupont', type: 'text' },
          { key: 'dateOfBirth', label: 'Date de naissance', placeholder: 'JJ/MM/AAAA', type: 'date' },
          { key: 'birthCountry', label: 'Pays de naissance', placeholder: 'Sélectionnez', type: 'select', options: COUNTRIES },
        ],
        multiEntryAddKey: 'addChildEntry',
      },

      childrenPassports: {
        id: 'childrenPassports',
        type: 'document',
        label: 'Veuillez télécharger le(s) passeport(s) de votre (vos) enfant(s)',
        indication: 'Téléchargez une copie du passeport de chaque enfant (page d\'identité).',
        help: 'Vous pouvez télécharger plusieurs fichiers. Formats acceptés : JPG, PNG, PDF.',
        isRequired: true,
        successMessage: 'Passeport(s) téléchargé(s)',
        section: 'Informations Personnelles et Familiales',
        dependsOn: { questionId: 'hasChildren', value: 'oui' },
      },

      childrenAccompany: {
        id: 'childrenAccompany',
        type: 'yes-no',
        label: 'Vos enfants vous accompagnent-ils au Canada ?',
        indication: 'Indiquez si vos enfants seront inclus dans votre demande d\'immigration.',
        help: 'Si certains enfants vous accompagnent et d\'autres non, répondez Oui et précisez dans la section enfants.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Informations Personnelles et Familiales',
        dependsOn: { questionId: 'hasChildren', value: 'oui' },
      },

      hasOtherDependants: {
        id: 'hasOtherDependants',
        type: 'yes-no',
        label: 'Avez-vous d\'autres personnes à charge ?',
        indication: 'Personnes qui dépendent financièrement de vous (autre que conjoint et enfants).',
        help: 'Par exemple : parents âgés, frères/sœurs à charge, etc.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Informations Personnelles et Familiales',
      },

      otherDependantsDetails: {
        id: 'otherDependantsDetails',
        type: 'textarea',
        label: 'Veuillez préciser les informations relatives à vos autres personnes à charge',
        indication: 'Indiquez le nom, le lien de parenté et la situation de chaque personne à charge.',
        help: 'Fournissez autant de détails que possible.',
        isRequired: true,
        successMessage: 'Informations enregistrées',
        section: 'Informations Personnelles et Familiales',
        dependsOn: { questionId: 'hasOtherDependants', value: 'oui' },
      },

      hasFamilyInCanada: {
        id: 'hasFamilyInCanada',
        type: 'yes-no',
        label: 'Avez-vous des membres de votre famille qui résident au Canada et qui sont citoyens canadiens ou résidents permanents ?',
        indication: 'Indiquez si vous avez de la famille proche au Canada avec un statut permanent.',
        help: 'Cela inclut les parents, frères, sœurs, oncles, tantes, cousins, etc.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Informations Personnelles et Familiales',
      },

      familyInCanadaDetails: {
        id: 'familyInCanadaDetails',
        type: 'multi-entry',
        label: 'Veuillez fournir les informations sur les membres de votre famille résidant au Canada',
        indication: 'Ajoutez chaque membre de votre famille au Canada.',
        help: 'Incluez le lien de parenté (parent, frère, sœur, etc.) et la ville où ils résident.',
        isRequired: true,
        successMessage: 'Informations enregistrées',
        section: 'Informations Personnelles et Familiales',
        dependsOn: { questionId: 'hasFamilyInCanada', value: 'oui' },
        multiEntryFields: [
          { key: 'name', label: 'Nom complet', placeholder: 'Jean Dupont', type: 'text', colSpan: 2 },
          { key: 'relationship', label: 'Lien de parenté', placeholder: 'Frère, Sœur, Parent...', type: 'text' },
          { key: 'cityProvince', label: 'Ville / Province', placeholder: 'Montréal, QC', type: 'text' },
        ],
        multiEntryAddKey: 'addFamilyMemberEntry',
      },

      // ── Section: Antécédents et Sécurité ──

      wasMilitary: {
        id: 'wasMilitary',
        type: 'yes-no',
        label: 'Avez-vous déjà fait partie d\'une force militaire, d\'une unité de défense civile, d\'un corps de police ou d\'un service de renseignement ?',
        indication: 'Indiquez toute affiliation passée ou présente.',
        help: 'Cela inclut le service militaire obligatoire.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Antécédents et Sécurité',
      },

      militaryDetails: {
        id: 'militaryDetails',
        type: 'textarea',
        label: 'Précisez votre service militaire ou policier',
        indication: 'Indiquez les dates, le pays, l\'unité et le grade.',
        help: 'Exemple : 2010-2012, France, Armée de terre, Caporal.',
        isRequired: true,
        successMessage: 'Détails enregistrés',
        section: 'Antécédents et Sécurité',
        dependsOn: { questionId: 'wasMilitary', value: 'oui' },
      },

      witnessedMistreatment: {
        id: 'witnessedMistreatment',
        type: 'yes-no',
        label: 'Avez-vous déjà été témoin de mauvais traitements infligés à des prisonniers ou à des civils, à des actes de pillage ou de profanation de lieux religieux ?',
        indication: 'Répondez honnêtement, cela fait partie de l\'évaluation de sécurité.',
        help: 'Cela peut inclure des événements survenus pendant un service militaire ou civil.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Antécédents et Sécurité',
      },

      witnessedMistreatmentDetails: {
        id: 'witnessedMistreatmentDetails',
        type: 'textarea',
        label: 'Précisez les mauvais traitements dont vous avez été témoin',
        indication: 'Décrivez les circonstances, le lieu et la date.',
        help: 'Fournissez autant de détails que possible.',
        isRequired: true,
        successMessage: 'Détails enregistrés',
        section: 'Antécédents et Sécurité',
        dependsOn: { questionId: 'witnessedMistreatment', value: 'oui' },
      },

      wasGovernmentOfficial: {
        id: 'wasGovernmentOfficial',
        type: 'yes-no',
        label: 'Avez-vous déjà occupé un poste au sein d\'un gouvernement ou d\'un organisme judiciaire ?',
        indication: 'Indiquez tout poste gouvernemental ou judiciaire passé ou présent.',
        help: 'Cela inclut les postes élus, nommés ou fonctionnaires.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Antécédents et Sécurité',
      },

      governmentOfficialDetails: {
        id: 'governmentOfficialDetails',
        type: 'textarea',
        label: 'Précisez votre poste gouvernemental ou judiciaire',
        indication: 'Indiquez le poste, l\'organisme, le pays et les dates.',
        help: 'Exemple : Juge, Tribunal de Casablanca, Maroc, 2015-2020.',
        isRequired: true,
        successMessage: 'Détails enregistrés',
        section: 'Antécédents et Sécurité',
        dependsOn: { questionId: 'wasGovernmentOfficial', value: 'oui' },
      },

      wasPoliticalMember: {
        id: 'wasPoliticalMember',
        type: 'yes-no',
        label: 'Avez-vous déjà été membre d\'un parti politique ou d\'un groupe ayant utilisé ou prôné la violence pour atteindre des objectifs politiques ou religieux ?',
        indication: 'Indiquez toute affiliation passée ou présente.',
        help: 'Cela inclut les groupes considérés comme terroristes ou extrémistes.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Antécédents et Sécurité',
      },

      politicalMemberDetails: {
        id: 'politicalMemberDetails',
        type: 'textarea',
        label: 'Précisez votre affiliation politique',
        indication: 'Indiquez le nom du groupe, le pays, les dates et votre rôle.',
        help: 'Fournissez autant de détails que possible.',
        isRequired: true,
        successMessage: 'Détails enregistrés',
        section: 'Antécédents et Sécurité',
        dependsOn: { questionId: 'wasPoliticalMember', value: 'oui' },
      },

      wasWarCriminal: {
        id: 'wasWarCriminal',
        type: 'yes-no',
        label: 'Avez-vous déjà commis, été accusé d\'avoir commis ou été impliqué dans un crime contre l\'humanité ou un crime de guerre ?',
        indication: 'Répondez honnêtement.',
        help: 'Cela inclut les accusations formelles ou informelles.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Antécédents et Sécurité',
      },

      warCriminalDetails: {
        id: 'warCriminalDetails',
        type: 'textarea',
        label: 'Précisez les crimes contre l\'humanité ou crimes de guerre',
        indication: 'Décrivez les circonstances, le lieu et la date.',
        help: 'Fournissez autant de détails que possible.',
        isRequired: true,
        successMessage: 'Détails enregistrés',
        section: 'Antécédents et Sécurité',
        dependsOn: { questionId: 'wasWarCriminal', value: 'oui' },
      },

      wasArrested: {
        id: 'wasArrested',
        type: 'yes-no',
        label: 'Avez-vous déjà été arrêté pour une infraction criminelle ?',
        indication: 'Indiquez toute arrestation, même si vous avez été acquitté.',
        help: 'Cela inclut les infractions mineures et les contraventions graves.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Antécédents et Sécurité',
      },

      arrestedDetails: {
        id: 'arrestedDetails',
        type: 'textarea',
        label: 'Précisez votre arrestation',
        indication: 'Indiquez la date, le pays, l\'infraction et l\'issue (condamnation, acquittement, etc.).',
        help: 'Exemple : 2018, France, excès de vitesse, amende.',
        isRequired: true,
        successMessage: 'Détails enregistrés',
        section: 'Antécédents et Sécurité',
        dependsOn: { questionId: 'wasArrested', value: 'oui' },
      },

      // ── Section: Emploi et Engagement ──

      intentControlledCompany: {
        id: 'intentControlledCompany',
        type: 'yes-no',
        label: 'Avez-vous l\'intention d\'occuper l\'emploi offert au Québec dans une entreprise sur laquelle vous exercez un contrôle ?',
        indication: 'Indiquez si vous avez un lien de propriété ou de contrôle avec l\'entreprise qui vous emploie.',
        help: 'Par exemple : vous êtes actionnaire majoritaire, administrateur, ou propriétaire de l\'entreprise.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Emploi et Engagement',
      },

      controlledCompanyDetails: {
        id: 'controlledCompanyDetails',
        type: 'textarea',
        label: 'Précisez le contrôle que vous exercez sur l\'entreprise',
        indication: 'Décrivez votre rôle et le type de contrôle (propriétaire, actionnaire, etc.).',
        help: 'Indiquez le pourcentage de parts détenues si applicable.',
        isRequired: true,
        successMessage: 'Détails enregistrés',
        section: 'Emploi et Engagement',
        dependsOn: { questionId: 'intentControlledCompany', value: 'oui' },
      },

      acceptIrccContact: {
        id: 'acceptIrccContact',
        type: 'yes-no',
        label: 'Acceptez-vous qu\'IRCC ou un organisme mandaté communique avec vous à l\'avenir ?',
        indication: 'IRCC pourra vous contacter pour des informations supplémentaires ou des mises à jour.',
        help: 'Il est recommandé d\'accepter pour faciliter le traitement de votre dossier.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Emploi et Engagement',
      },

      // ── Section: Consentement et Validation ──

      consentAccuracy: {
        id: 'consentAccuracy',
        type: 'checkbox',
        label: 'Déclaration d\'exactitude des informations',
        indication: 'Je déclare que toutes les informations fournies dans ce formulaire sont exactes et complètes au meilleur de ma connaissance. Je comprends que toute fausse déclaration peut entraîner le rejet de ma demande.',
        help: 'Cette déclaration est requise pour soumettre votre demande.',
        isRequired: true,
        successMessage: 'Déclaration acceptée',
        section: 'Consentement et Validation',
      },

      consentDataSharing: {
        id: 'consentDataSharing',
        type: 'checkbox',
        label: 'Consentement à la collecte et communication des données',
        indication: 'J\'autorise la collecte, l\'utilisation et la communication de mes renseignements personnels à IRCC, au MIFI et à tout organisme mandaté aux fins du traitement de ma demande d\'immigration.',
        help: 'Vos données seront traitées conformément aux lois sur la protection des renseignements personnels.',
        isRequired: true,
        successMessage: 'Consentement accepté',
        section: 'Consentement et Validation',
      },

      signatureField: {
        id: 'signatureField',
        type: 'text',
        label: 'Signature',
        indication: 'Tapez votre nom complet en guise de signature électronique.',
        help: 'En tapant votre nom, vous confirmez avoir lu et accepté les déclarations ci-dessus.',
        isRequired: true,
        successMessage: 'Signature enregistrée',
        section: 'Consentement et Validation',
      },
    },
    order: [
      // Informations Personnelles et Familiales
      'hasChildren',
      'childrenInfo',
      'childrenPassports',
      'childrenAccompany',
      'hasOtherDependants',
      'otherDependantsDetails',
      'hasFamilyInCanada',
      'familyInCanadaDetails',
      // Antécédents et Sécurité
      'wasMilitary',
      'militaryDetails',
      'witnessedMistreatment',
      'witnessedMistreatmentDetails',
      'wasGovernmentOfficial',
      'governmentOfficialDetails',
      'wasPoliticalMember',
      'politicalMemberDetails',
      'wasWarCriminal',
      'warCriminalDetails',
      'wasArrested',
      'arrestedDetails',
      // Emploi et Engagement
      'intentControlledCompany',
      'controlledCompanyDetails',
      'acceptIrccContact',
      // Consentement et Validation
      'consentAccuracy',
      'consentDataSharing',
      'signatureField',
    ],
  },

  // Application ID 2
  2: {
    questions: {
      // ── Section: Informations Personnelles et Familiales ──

      d2FullName: {
        id: 'd2FullName',
        type: 'text',
        label: 'Votre nom complet',
        indication: 'Indiquez votre prénom et nom de famille tels qu\'ils apparaissent sur vos documents officiels.',
        help: 'Assurez-vous que l\'orthographe correspond exactement à votre passeport.',
        isRequired: true,
        successMessage: 'Nom enregistré',
        section: 'Informations Personnelles et Familiales',
        example: 'Jean Dupont',
      },

      d2HasChildren: {
        id: 'd2HasChildren',
        type: 'yes-no',
        label: 'Avez-vous des enfants ?',
        indication: 'Indiquez si vous avez des enfants, même s\'ils ne vous accompagnent pas.',
        help: 'Incluez tous vos enfants biologiques ou adoptés, quel que soit leur âge.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Informations Personnelles et Familiales',
      },

      d2ChildrenInfo: {
        id: 'd2ChildrenInfo',
        type: 'multi-entry',
        label: 'Veuillez remplir les informations relatives à vos enfants',
        indication: 'Ajoutez chaque enfant avec ses informations.',
        help: 'Incluez tous vos enfants, y compris ceux qui ne vous accompagnent pas.',
        isRequired: true,
        successMessage: 'Informations des enfants enregistrées',
        section: 'Informations Personnelles et Familiales',
        dependsOn: { questionId: 'd2HasChildren', value: 'oui' },
        multiEntryFields: [
          { key: 'firstName', label: 'Prénom', placeholder: 'Jean', type: 'text' },
          { key: 'lastName', label: 'Nom', placeholder: 'Dupont', type: 'text' },
          { key: 'dateOfBirth', label: 'Date de naissance', placeholder: 'JJ/MM/AAAA', type: 'date' },
          { key: 'birthCountry', label: 'Pays de naissance', placeholder: 'Sélectionnez', type: 'select', options: COUNTRIES },
        ],
        multiEntryAddKey: 'addChildEntry',
      },

      d2ParentsInfo: {
        id: 'd2ParentsInfo',
        type: 'multi-entry',
        label: 'Veuillez remplir les informations relatives à vos parents',
        indication: 'Ajoutez les informations de votre père et de votre mère.',
        help: 'Si un parent est décédé, indiquez les informations connues.',
        isRequired: true,
        successMessage: 'Informations des parents enregistrées',
        section: 'Informations Personnelles et Familiales',
        multiEntryFields: [
          { key: 'firstName', label: 'Prénom', placeholder: 'Marie', type: 'text' },
          { key: 'lastName', label: 'Nom', placeholder: 'Dupont', type: 'text' },
          { key: 'dateOfBirth', label: 'Date de naissance', placeholder: 'JJ/MM/AAAA', type: 'date' },
          { key: 'birthCountry', label: 'Pays de naissance', placeholder: 'Sélectionnez', type: 'select', options: COUNTRIES },
        ],
        multiEntryAddKey: 'addParentEntry',
      },

      // ── Section: Antécédents médicaux et séjour au Canada ──

      d2Tuberculosis: {
        id: 'd2Tuberculosis',
        type: 'yes-no',
        label: 'Au cours des deux dernières années, avez-vous ou un membre de votre famille eu la tuberculose, ou été en contact étroit avec une personne atteinte de la tuberculose ?',
        indication: 'Indiquez tout contact avec la tuberculose.',
        help: 'Cela inclut un diagnostic, un test positif ou un contact étroit avec une personne atteinte.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Antécédents médicaux et séjour au Canada',
      },

      d2PhysicalMentalDisorder: {
        id: 'd2PhysicalMentalDisorder',
        type: 'yes-no',
        label: 'Avez-vous un trouble physique ou mental qui nécessiterait des services sociaux ou des soins de santé autres que des médicaments pendant votre séjour au Canada ?',
        indication: 'Indiquez tout trouble nécessitant un suivi médical ou social.',
        help: 'Cela n\'affectera pas nécessairement votre admissibilité.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Antécédents médicaux et séjour au Canada',
      },

      d2CanadaHistoryIssues: {
        id: 'd2CanadaHistoryIssues',
        type: 'yes-no',
        label: 'Êtes-vous déjà resté au Canada après l\'expiration de votre statut, fréquenté l\'école sans permis d\'études ou travaillé sans permis de travail ?',
        indication: 'Indiquez tout dépassement de statut ou travail/étude sans autorisation.',
        help: 'Répondez honnêtement, cela fait partie de l\'évaluation d\'admissibilité.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Antécédents médicaux et séjour au Canada',
      },

      d2WasRefusedVisa: {
        id: 'd2WasRefusedVisa',
        type: 'yes-no',
        label: 'Vous a-t-on déjà refusé un visa ou un permis, interdit l\'entrée ou demandé de quitter le Canada ou un autre pays/territoire ?',
        indication: 'Indiquez tout refus de visa ou d\'entrée.',
        help: 'Incluez les demandes retirées avant une décision.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Antécédents médicaux et séjour au Canada',
      },

      d2PreviousCanadaApplication: {
        id: 'd2PreviousCanadaApplication',
        type: 'yes-no',
        label: 'Avez-vous déjà présenté une demande pour entrer ou demeurer au Canada ?',
        indication: 'Indiquez toute demande antérieure d\'immigration ou de visa pour le Canada.',
        help: 'Par exemple : sélection temporaire pour travail, visa de visiteur, permis d\'études, etc.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Antécédents médicaux et séjour au Canada',
      },

      // ── Section: Sécurité et antécédents judiciaires ──

      d2HasCriminalRecord: {
        id: 'd2HasCriminalRecord',
        type: 'yes-no',
        label: 'Avez-vous déjà commis, été arrêté, accusé ou reconnu coupable d\'une infraction pénale dans un pays ou territoire ?',
        indication: 'Indiquez toute infraction pénale, même mineure.',
        help: 'Incluez les infractions pour lesquelles vous avez été acquitté.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Sécurité et antécédents judiciaires',
      },

      d2WasMilitary: {
        id: 'd2WasMilitary',
        type: 'yes-no',
        label: 'Avez-vous fait partie d\'une armée, d\'une milice, d\'une unité de défense civile, d\'un service de renseignements ou d\'un corps de police ?',
        indication: 'Indiquez toute affiliation passée ou présente.',
        help: 'Cela inclut le service militaire obligatoire.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Sécurité et antécédents judiciaires',
      },

      d2WasPoliticalMember: {
        id: 'd2WasPoliticalMember',
        type: 'yes-no',
        label: 'Êtes-vous ou avez-vous déjà été membre ou affilié à un parti politique, un groupe ou une organisation ayant utilisé ou prôné la violence, ou impliqués dans des activités criminelles ?',
        indication: 'Indiquez toute affiliation passée ou présente.',
        help: 'Cela inclut les groupes considérés comme terroristes ou extrémistes.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Sécurité et antécédents judiciaires',
      },

      d2WitnessedMistreatment: {
        id: 'd2WitnessedMistreatment',
        type: 'yes-no',
        label: 'Avez-vous déjà été témoin ou participé à des mauvais traitements infligés à des prisonniers ou des civils, à des actes de pillage ou de profanation de lieux religieux ?',
        indication: 'Répondez honnêtement, cela fait partie de l\'évaluation de sécurité.',
        help: 'Cela peut inclure des événements survenus pendant un service militaire ou civil.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Sécurité et antécédents judiciaires',
      },

      // ── Section: Consentement et Validation ──

      d2ConsentAccuracy: {
        id: 'd2ConsentAccuracy',
        type: 'checkbox',
        label: 'Déclaration d\'exactitude des informations',
        indication: 'Je déclare que toutes les informations fournies dans ce formulaire sont exactes et complètes au meilleur de ma connaissance. Je comprends que toute fausse déclaration peut entraîner le rejet de ma demande.',
        help: 'Cette déclaration est requise pour soumettre votre demande.',
        isRequired: true,
        successMessage: 'Déclaration acceptée',
        section: 'Consentement et Validation',
      },

      d2ConsentDataSharing: {
        id: 'd2ConsentDataSharing',
        type: 'checkbox',
        label: 'Consentement à la communication des données',
        indication: 'J\'autorise la collecte, l\'utilisation et la communication de mes renseignements personnels aux instances gouvernementales comme l\'IRCC et le MIFI aux fins du traitement de ma demande d\'immigration.',
        help: 'Vos données seront traitées conformément aux lois sur la protection des renseignements personnels.',
        isRequired: true,
        successMessage: 'Consentement accepté',
        section: 'Consentement et Validation',
      },

      d2Signature: {
        id: 'd2Signature',
        type: 'text',
        label: 'Signature',
        indication: 'Tapez votre nom complet en guise de signature électronique.',
        help: 'En tapant votre nom, vous confirmez avoir lu et accepté les déclarations ci-dessus.',
        isRequired: true,
        successMessage: 'Signature enregistrée',
        section: 'Consentement et Validation',
      },
    },
    order: [
      // Informations Personnelles et Familiales
      'd2FullName',
      'd2HasChildren',
      'd2ChildrenInfo',
      'd2ParentsInfo',
      // Antécédents médicaux et séjour au Canada
      'd2Tuberculosis',
      'd2PhysicalMentalDisorder',
      'd2CanadaHistoryIssues',
      'd2WasRefusedVisa',
      'd2PreviousCanadaApplication',
      // Sécurité et antécédents judiciaires
      'd2HasCriminalRecord',
      'd2WasMilitary',
      'd2WasPoliticalMember',
      'd2WitnessedMistreatment',
      // Consentement et Validation
      'd2ConsentAccuracy',
      'd2ConsentDataSharing',
      'd2Signature',
    ],
  },

  // Application ID 3
  3: {
    questions: {
      // ── Section: Informations Personnelles et Familiales ──

      d3FullName: {
        id: 'd3FullName',
        type: 'text',
        label: 'Votre nom complet',
        indication: 'Veuillez indiquer le nom de la personne pour qui la demande est présentée (par exemple : votre enfant). Prénom et Nom de famille.',
        help: 'Assurez-vous que l\'orthographe correspond exactement aux documents officiels.',
        isRequired: true,
        successMessage: 'Nom enregistré',
        section: 'Informations Personnelles et Familiales',
        example: 'Jean Dupont',
      },

      d3HasChildren: {
        id: 'd3HasChildren',
        type: 'yes-no',
        label: 'Avez-vous des enfants ?',
        indication: 'Indiquez si vous avez des enfants.',
        help: 'Incluez tous vos enfants biologiques ou adoptés.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Informations Personnelles et Familiales',
      },

      d3ChildrenInfo: {
        id: 'd3ChildrenInfo',
        type: 'multi-entry',
        label: 'Veuillez remplir les informations relatives à vos enfants',
        indication: 'Ajoutez tous vos enfants qui vous accompagnent.',
        help: 'Incluez le prénom, nom, date de naissance et occupation de chaque enfant.',
        isRequired: true,
        successMessage: 'Informations des enfants enregistrées',
        section: 'Informations Personnelles et Familiales',
        dependsOn: { questionId: 'd3HasChildren', value: 'oui' },
        multiEntryFields: [
          { key: 'firstName', label: 'Prénom(s)', placeholder: 'Jean', type: 'text' },
          { key: 'lastName', label: 'Nom(s)', placeholder: 'Dupont', type: 'text' },
          { key: 'dateOfBirth', label: 'Date de naissance', placeholder: 'JJ/MM/AAAA', type: 'date' },
          { key: 'occupation', label: 'Occupation/profession', placeholder: 'Étudiant(e)', type: 'text' },
        ],
        multiEntryAddKey: 'addChildEntry',
      },

      d3ParentsInfo: {
        id: 'd3ParentsInfo',
        type: 'multi-entry',
        label: 'Veuillez remplir les informations relatives à vos parents',
        indication: 'Ajoutez les informations de votre père et de votre mère.',
        help: 'Si un parent est décédé, indiquez la date de décès dans le champ occupation.',
        isRequired: true,
        successMessage: 'Informations des parents enregistrées',
        section: 'Informations Personnelles et Familiales',
        multiEntryFields: [
          { key: 'firstName', label: 'Prénom(s)', placeholder: 'Marie', type: 'text' },
          { key: 'lastName', label: 'Nom(s)', placeholder: 'Dupont', type: 'text' },
          { key: 'dateOfBirth', label: 'Date de naissance', placeholder: 'JJ/MM/AAAA', type: 'date' },
          { key: 'occupation', label: 'Occupation/profession (si décédé, indiquez la date de décès)', placeholder: 'Enseignant(e) / Décédé(e) le 01/01/2020', type: 'text', colSpan: 2 },
        ],
        multiEntryAddKey: 'addParentEntry',
      },

      d3HasSiblings: {
        id: 'd3HasSiblings',
        type: 'yes-no',
        label: 'Avez-vous des frères et/ou sœurs ?',
        indication: 'Indiquez si vous avez des frères ou sœurs.',
        help: 'Incluez les demi-frères et demi-sœurs.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Informations Personnelles et Familiales',
      },

      d3SiblingsInfo: {
        id: 'd3SiblingsInfo',
        type: 'multi-entry',
        label: 'Veuillez remplir les informations relatives à vos frères et sœurs',
        indication: 'Ajoutez chaque frère ou sœur avec ses informations.',
        help: 'Indiquez s\'ils vous accompagnent au Canada.',
        isRequired: true,
        successMessage: 'Informations enregistrées',
        section: 'Informations Personnelles et Familiales',
        dependsOn: { questionId: 'd3HasSiblings', value: 'oui' },
        multiEntryFields: [
          { key: 'firstName', label: 'Prénom(s)', placeholder: 'Pierre', type: 'text' },
          { key: 'lastName', label: 'Nom(s)', placeholder: 'Dupont', type: 'text' },
          { key: 'dateOfBirth', label: 'Date de naissance', placeholder: 'JJ/MM/AAAA', type: 'date' },
          { key: 'accompanyingToCanada', label: 'Vous accompagne au Canada ?', placeholder: 'Sélectionnez', type: 'select', options: [{ code: 'oui', name: 'Oui', nameEn: 'Yes', nameEs: 'Sí' }, { code: 'non', name: 'Non', nameEn: 'No', nameEs: 'No' }] },
        ],
        multiEntryAddKey: 'addSiblingEntry',
      },

      // ── Section: Soutien financier et justificatifs ──

      d3FinancialSupport: {
        id: 'd3FinancialSupport',
        type: 'select',
        label: 'Qui prendra en charge vos besoins financiers pendant vos études au Canada ?',
        indication: 'Sélectionnez la personne ou l\'entité qui financera votre séjour.',
        help: 'Cela déterminera les documents justificatifs à fournir.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Soutien financier et justificatifs',
        options: [
          { code: 'moi-meme', name: 'Moi-même', nameEn: 'Myself', nameEs: 'Yo mismo(a)' },
          { code: 'parents', name: 'Mon/mes parent(s)', nameEn: 'My parent(s)', nameEs: 'Mi(s) padre(s)' },
          { code: 'tuteur', name: 'Mon tuteur légal', nameEn: 'My legal guardian', nameEs: 'Mi tutor legal' },
          { code: 'autre', name: 'Autre (à préciser)', nameEn: 'Other (please specify)', nameEs: 'Otro (por favor especifique)' },
        ],
      },

      d3FinancialSupportOther: {
        id: 'd3FinancialSupportOther',
        type: 'text',
        label: 'Précisez qui prendra en charge vos besoins financiers',
        indication: 'Indiquez le nom et le lien avec cette personne.',
        help: 'Par exemple : oncle, employeur, bourse d\'études, etc.',
        isRequired: true,
        successMessage: 'Information enregistrée',
        section: 'Soutien financier et justificatifs',
        dependsOn: { questionId: 'd3FinancialSupport', value: 'autre' },
      },

      d3CoverLetter: {
        id: 'd3CoverLetter',
        type: 'document',
        label: 'Lettre explicative',
        indication: 'Veuillez transmettre une lettre qui fait état de votre relation et qui atteste la prise en charge des frais (transport, subsistance, scolarité, logement).',
        help: 'La lettre doit être signée par le garant. Formats acceptés : PDF, JPG, PNG.',
        isRequired: true,
        successMessage: 'Lettre téléchargée',
        section: 'Soutien financier et justificatifs',
      },

      d3BankStatement: {
        id: 'd3BankStatement',
        type: 'document',
        label: 'Preuve de fonds (attestation bancaire)',
        indication: 'L\'attestation doit être celle du parent, du tuteur, du garant ou de l\'enfant lui-même.',
        help: 'Le document doit être récent (moins de 3 mois). Formats acceptés : PDF, JPG, PNG.',
        isRequired: true,
        successMessage: 'Attestation téléchargée',
        section: 'Soutien financier et justificatifs',
      },

      d3AdditionalFinancialDocs: {
        id: 'd3AdditionalFinancialDocs',
        type: 'document',
        label: 'Documents supplémentaires',
        indication: 'Tout document démontrant vos ressources ou votre situation financière.',
        help: 'Par exemple : relevés de compte, lettres de bourse, etc. Formats acceptés : PDF, JPG, PNG.',
        isRequired: false,
        successMessage: 'Documents téléchargés',
        section: 'Soutien financier et justificatifs',
      },

      d3GuarantorPassport: {
        id: 'd3GuarantorPassport',
        type: 'document',
        label: 'Passeport du garant',
        indication: 'Copie du passeport du parent, tuteur ou garant qui prend en charge les besoins financiers.',
        help: 'Téléchargez la page d\'identité du passeport. Formats acceptés : PDF, JPG, PNG.',
        isRequired: true,
        successMessage: 'Passeport téléchargé',
        section: 'Soutien financier et justificatifs',
      },

      d3GuarantorStatusProof: {
        id: 'd3GuarantorStatusProof',
        type: 'document',
        label: 'Preuve de statut au Canada',
        indication: 'Le cas échéant, preuve de statut du garant au Canada.',
        help: 'Par exemple : carte de résident permanent, permis de travail, etc. Formats acceptés : PDF, JPG, PNG.',
        isRequired: false,
        successMessage: 'Preuve téléchargée',
        section: 'Soutien financier et justificatifs',
      },

      d3FiliationProof: {
        id: 'd3FiliationProof',
        type: 'document',
        label: 'Preuve de filiation',
        indication: 'Document prouvant le lien avec le garant (acte de naissance ou de mariage). Ce n\'est pas obligatoire si le garant n\'est pas un membre de la famille.',
        help: 'Formats acceptés : PDF, JPG, PNG.',
        isRequired: false,
        successMessage: 'Preuve téléchargée',
        section: 'Soutien financier et justificatifs',
      },

      // ── Section: Scolarité et historique légal ──

      d3HasCAQ: {
        id: 'd3HasCAQ',
        type: 'yes-no',
        label: 'Avez-vous déjà obtenu un Certificat d\'acceptation du Québec (CAQ) ?',
        indication: 'Indiquez si vous possédez déjà un CAQ.',
        help: 'Le CAQ est requis pour étudier au Québec.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Scolarité et historique légal',
      },

      d3CAQDocument: {
        id: 'd3CAQDocument',
        type: 'document',
        label: 'Fournir le CAQ',
        indication: 'Le cas échéant, téléchargez votre Certificat d\'acceptation du Québec.',
        help: 'Formats acceptés : PDF, JPG, PNG.',
        isRequired: true,
        successMessage: 'CAQ téléchargé',
        section: 'Scolarité et historique légal',
        dependsOn: { questionId: 'd3HasCAQ', value: 'oui' },
      },

      d3AdmissionLetter: {
        id: 'd3AdmissionLetter',
        type: 'document',
        label: 'Lettre d\'admission',
        indication: 'Lettre d\'admission dans un établissement scolaire.',
        help: 'La lettre doit provenir de l\'établissement canadien désigné (DLI). Formats acceptés : PDF, JPG, PNG.',
        isRequired: true,
        successMessage: 'Lettre téléchargée',
        section: 'Scolarité et historique légal',
      },

      d3SchoolTranscripts: {
        id: 'd3SchoolTranscripts',
        type: 'document',
        label: 'Attestation de fréquentation scolaire et relevés de notes',
        indication: 'Téléchargez vos attestations et relevés de notes.',
        help: 'Incluez tous les documents pertinents de vos études récentes. Formats acceptés : PDF, JPG, PNG.',
        isRequired: true,
        successMessage: 'Documents téléchargés',
        section: 'Scolarité et historique légal',
      },

      d3AdditionalStudyDocs: {
        id: 'd3AdditionalStudyDocs',
        type: 'document',
        label: 'Documents complémentaires',
        indication: 'Tout document jugé pertinent pour la demande de permis d\'études.',
        help: 'Par exemple : plan d\'études, lettre de motivation, etc. Formats acceptés : PDF, JPG, PNG.',
        isRequired: false,
        successMessage: 'Documents téléchargés',
        section: 'Scolarité et historique légal',
      },

      // ── Section: Santé et Sécurité ──

      d3Tuberculosis: {
        id: 'd3Tuberculosis',
        type: 'yes-no',
        label: 'Au cours des deux dernières années, avez-vous ou un membre de votre famille eu la tuberculose ou été en contact avec une personne atteinte ?',
        indication: 'Indiquez tout contact avec la tuberculose.',
        help: 'Cela inclut un diagnostic, un test positif ou un contact étroit avec une personne atteinte.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Santé et Sécurité',
      },

      d3PhysicalMentalDisorder: {
        id: 'd3PhysicalMentalDisorder',
        type: 'yes-no',
        label: 'Avez-vous un trouble physique ou mental nécessitant des services sociaux ou des soins de santé autres que des médicaments ?',
        indication: 'Indiquez tout trouble nécessitant un suivi médical ou social.',
        help: 'Cela n\'affectera pas nécessairement votre admissibilité.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Santé et Sécurité',
      },

      d3CanadaHistoryIssues: {
        id: 'd3CanadaHistoryIssues',
        type: 'yes-no',
        label: 'Êtes-vous déjà resté au Canada après l\'expiration de votre statut, fréquenté l\'école ou travaillé sans permis ?',
        indication: 'Indiquez tout dépassement de statut ou travail/étude sans autorisation.',
        help: 'Répondez honnêtement, cela fait partie de l\'évaluation d\'admissibilité.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Santé et Sécurité',
      },

      d3WasRefusedVisa: {
        id: 'd3WasRefusedVisa',
        type: 'yes-no',
        label: 'Vous a-t-on déjà refusé un visa ou un permis, interdit l\'entrée ou demandé de quitter le Canada ou un autre pays ?',
        indication: 'Indiquez tout refus de visa ou d\'entrée.',
        help: 'Incluez les demandes retirées avant une décision.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Santé et Sécurité',
      },

      d3PreviousCanadaApplication: {
        id: 'd3PreviousCanadaApplication',
        type: 'yes-no',
        label: 'Avez-vous déjà présenté une demande pour entrer ou demeurer au Canada ?',
        indication: 'Indiquez toute demande antérieure d\'immigration ou de visa pour le Canada.',
        help: 'Cela inclut les demandes de visa de visiteur, permis d\'études, permis de travail, etc.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Santé et Sécurité',
      },

      d3HasCriminalRecord: {
        id: 'd3HasCriminalRecord',
        type: 'yes-no',
        label: 'Avez-vous déjà commis, été arrêté, accusé ou reconnu coupable d\'une infraction pénale ?',
        indication: 'Indiquez toute infraction pénale, même mineure.',
        help: 'Incluez les infractions pour lesquelles vous avez été acquitté.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Santé et Sécurité',
      },

      d3WasMilitary: {
        id: 'd3WasMilitary',
        type: 'yes-no',
        label: 'Avez-vous fait partie d\'une armée, d\'une milice, d\'un service de renseignements ou d\'un corps de police ?',
        indication: 'Indiquez toute affiliation passée ou présente.',
        help: 'Cela inclut le service militaire obligatoire.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Santé et Sécurité',
      },

      d3WasPoliticalMember: {
        id: 'd3WasPoliticalMember',
        type: 'yes-no',
        label: 'Êtes-vous ou avez-vous été membre d\'un groupe ayant utilisé la violence ou impliqué dans des activités criminelles ?',
        indication: 'Indiquez toute affiliation passée ou présente.',
        help: 'Cela inclut les groupes considérés comme terroristes ou extrémistes.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Santé et Sécurité',
      },

      d3WitnessedMistreatment: {
        id: 'd3WitnessedMistreatment',
        type: 'yes-no',
        label: 'Avez-vous été témoin ou participé à des mauvais traitements, pillages ou profanations de lieux religieux ?',
        indication: 'Répondez honnêtement, cela fait partie de l\'évaluation de sécurité.',
        help: 'Cela peut inclure des événements survenus pendant un service militaire ou civil.',
        isRequired: true,
        successMessage: 'Réponse enregistrée',
        section: 'Santé et Sécurité',
      },
    },
    order: [
      // Informations Personnelles et Familiales
      'd3FullName',
      'd3HasChildren',
      'd3ChildrenInfo',
      'd3ParentsInfo',
      'd3HasSiblings',
      'd3SiblingsInfo',
      // Soutien financier et justificatifs
      'd3FinancialSupport',
      'd3FinancialSupportOther',
      'd3CoverLetter',
      'd3BankStatement',
      'd3AdditionalFinancialDocs',
      'd3GuarantorPassport',
      'd3GuarantorStatusProof',
      'd3FiliationProof',
      // Scolarité et historique légal
      'd3HasCAQ',
      'd3CAQDocument',
      'd3AdmissionLetter',
      'd3SchoolTranscripts',
      'd3AdditionalStudyDocs',
      // Santé et Sécurité
      'd3Tuberculosis',
      'd3PhysicalMentalDisorder',
      'd3CanadaHistoryIssues',
      'd3WasRefusedVisa',
      'd3PreviousCanadaApplication',
      'd3HasCriminalRecord',
      'd3WasMilitary',
      'd3WasPoliticalMember',
      'd3WitnessedMistreatment',
    ],
  },
};

/**
 * Returns the demande-specific questions and order for a given application ID.
 * Falls back to empty questions/order if the application ID is not found.
 */
export function getDemandeQuestions(applicationId: number | null | undefined): DemandeQuestionSet {
  if (applicationId == null || !DEMANDE_QUESTIONS[applicationId]) {
    return { questions: {}, order: [] };
  }
  return DEMANDE_QUESTIONS[applicationId];
}
